/**
 * AnimationDialog - Dialog for playing animations.
 * Shows animation canvas with full animation controls.
 * Uses React Query for play content fetching with proper caching.
 */

import { Dialog, DialogContent, DialogTitle } from '../ui/Dialog'
import { DialogCloseButton } from '../ui/DialogCloseButton'
import { AnimationProvider, useAnimation } from '../../contexts/AnimationContext'
import { AnimationCanvas } from './AnimationCanvas'
import { AnimationControls } from './AnimationControls'
import { useAnimationTiming } from '@/hooks/UseAnimationTiming'
import { usePlayContent } from '@/api/queries/PlayQueries'
import { usePlayLoader } from '@/hooks/UsePlayLoader'
import './animation-dialog.css'

type AnimationDialogProps = {
	isOpen: boolean
	onClose: () => void
	playId: string | null
	playName?: string
}

function AnimationDialogContent({
									playId,
									playName,
									onClose,
								}: {
	playId: string | null
	playName?: string
	onClose: () => void
}) {
	const { data: playContent, isLoading } = usePlayContent(
		playId,
		{ enabled: playId !== null },
	)

	const playTiming = useAnimationTiming(
		playContent
			? {
				id: playContent.id,
				players: playContent.players,
				drawings: playContent.drawings,
			}
			: null,
	)

	const { loadPlay, stop } = useAnimation()

	usePlayLoader({
		playTiming,
		playId: playContent?.id ?? null,
		stop,
		loadPlay,
	})

	const displayName = playContent?.name || playName || 'Play Animation'

	return (
		<div className="animation-dialog-content">
			<div className="animation-dialog-header">
				<span className="animation-dialog-title">{displayName}</span>
				<DialogCloseButton onClose={onClose} />
			</div>

			{isLoading ? (
				<div className="animation-dialog-loading">
					<p className="animation-dialog-loading-text">Loading animation...</p>
				</div>
			) : !playContent ? (
				<div className="animation-dialog-error">
					<p className="animation-dialog-error-text">Failed to load animation</p>
				</div>
			) : (
				<>
					<div className="animation-dialog-canvas-container">
						<AnimationCanvas
							drawings={playContent.drawings}
							players={playContent.players}
						/>
					</div>

					<div className="animation-dialog-controls">
						<AnimationControls />
					</div>
				</>
			)}
		</div>
	)
}

export function AnimationDialog({
									isOpen,
									onClose,
									playId,
									playName,
								}: AnimationDialogProps) {
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="animation-dialog-wrapper">
				<DialogTitle className="sr-only">{playName || 'Play Animation'}</DialogTitle>
				<AnimationProvider>
					<AnimationDialogContent playId={playId} playName={playName} onClose={onClose} />
				</AnimationProvider>
			</DialogContent>
		</Dialog>
	)
}
